import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';
import { Routes } from '@common/constants';
import { useSinglePageCheckoutSupported } from '@common/hooks/useSinglePageCheckoutSupported';
import { useTranslations } from '@lang';
import { analytics, formatBasketItems, formatPrice } from '@features/analytics';
import { startSinglePageCheckout } from '@store/basket/actions';
import { basketSelectors } from '@store/basket/selectors';
import { isBasketItemExpired } from '@store/basket/utils/isBasketItemExpired';
import { getLanguageCodeFromLocale } from '@utils/getLanguageCodeFromLocale';

export function useStartCheckout() {
  const router = useRouter();
  const dispatch = useDispatch();
  const { locale } = useTranslations();
  const language = getLanguageCodeFromLocale(locale);
  const isSinglePageCheckoutSupported = useSinglePageCheckoutSupported();
  const basketDetails = useSelector(basketSelectors.getBasketDetails) as any;
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);

  const startCheckout = useCallback(
    async (options?: { replace?: boolean }) => {
      if (isStartingCheckout || !basketDetails) return;

      const goTo = (pathname: string) =>
        options?.replace ? router.replace({ pathname }) : router.push({ pathname });

      analytics.track({
        event: 'begin_checkout',
        params: {
          currency: basketDetails.total?.currencyCode,
          value: formatPrice(basketDetails.total?.amount),
          items: formatBasketItems(basketDetails.items || []),
          coupon: basketDetails.promoCode || undefined,
        },
      });

      if (!isSinglePageCheckoutSupported) {
        goTo(Routes.CHECKOUT);
        return;
      }

      const hasExpiredItems = basketDetails.items?.some((item: { expireAt?: string }) =>
        isBasketItemExpired(item)
      );
      if (hasExpiredItems) {
        goTo(Routes.BASKET);
        return;
      }

      setIsStartingCheckout(true);
      try {
        const result = (await (dispatch as (action: unknown) => Promise<unknown>)(
          startSinglePageCheckout(language)
        )) as { orderReference?: string } | undefined;

        goTo(result?.orderReference ? Routes.CHECKOUT : Routes.BASKET);
      } finally {
        setIsStartingCheckout(false);
      }
    },
    [isStartingCheckout, basketDetails, isSinglePageCheckoutSupported, dispatch, router, language]
  );

  return { startCheckout, isStartingCheckout };
}
